/**
 * Per-device user input settings (invert Y, mouse / touch sensitivity).
 *
 * Settings live in `localStorage` and are applied by `InputManager` every
 * frame. The pause-menu's "Controls" category provides a UI to toggle
 * them while the game is running, so changes take effect immediately
 * without restarting the session.
 *
 * Why two separate invert-Y flags (mouse + touch)?
 *   - Desktop pilots that grew up on flight sims often want inverted
 *     mouse Y but standard touch (so when they pick up a phone the
 *     stick still pushes "up to look up").
 *   - Touch pilots are split for the same reason: console twin-stick
 *     muscle memory differs by player.
 * Treating the two as independent is cheap and removes any "the wrong
 * input feels backwards" surprise after a single global toggle.
 *
 * Why two separate sensitivity multipliers (mouse + touch)?
 *   - The mouse and the touch joystick have radically different per-unit
 *     gain (mouse uses raw pixel deltas, touch uses normalized stick
 *     deflection over a per-second look-rate), so a single shared
 *     "sensitivity" number would feel different on each surface.
 *   - The default touch value is intentionally < 1 because the previous
 *     full-rate baseline (~155°/sec at full deflection) was reported as
 *     too twitchy on small phones; cutting it to 0.7× brings full
 *     deflection down to ~108°/sec, which is closer to a console
 *     twin-stick default. Pilots that want the old feel can crank the
 *     slider back to 1.0+ in the options menu.
 */

const STORAGE_KEY = 'vs_input_settings_v1';

/**
 * Base sensitivity scalar applied to raw mouseDelta values to produce
 * radians of camera rotation per pointer pixel. Exposed so the pure
 * helper `effectiveMouseSensitivity` can multiply it by the user-tunable
 * multiplier and produce a single per-pixel coefficient the InputManager
 * (and any unit tests) can apply.
 */
export const BASE_MOUSE_SENSITIVITY = 0.002;

/**
 * Base maximum angular velocity (rad/sec) the right touch stick produces
 * at full deflection. Mirrors `TOUCH_LOOK_RATE` in `input-manager.ts`;
 * kept here so the same value can be reused by aim-assist code and by
 * tests without pulling in the whole input manager. The user-tunable
 * `touchSensitivity` is a multiplier on top of this.
 */
export const BASE_TOUCH_LOOK_RATE = 2.7;

/**
 * Lower and upper bounds the sensitivity slider accepts. Picked so a
 * pilot can never push the value to 0 (which would silently break their
 * controls and look like a bug) and can never crank it absurdly high
 * either — the goal is finer tuning, not a "twitch shooter" budget.
 * Bounded values also keep PvP parity: even the upper bound stays
 * comfortably inside the look-rate envelope a desktop mouse already has
 * via DPI, so touch can't out-spin mouse-and-keyboard at max setting.
 */
export const MIN_SENSITIVITY = 0.25;
export const MAX_SENSITIVITY = 2.0;

export interface InputSettings {
  /**
   * Invert vertical-axis input from the mouse / desktop look. When true,
   * pulling the mouse down looks up. Applied to the mouseDeltaY-derived
   * pitch in `InputManager.getState`.
   */
  invertYMouse: boolean;
  /**
   * Invert vertical-axis input from the right touch joystick (mobile
   * aim). Independent from the mouse flag so the same player can have
   * the desktop/mouse layout inverted without their phone session
   * picking up the inversion.
   */
  invertYTouch: boolean;
  /**
   * Multiplier on the per-pixel mouse sensitivity. 1.0 = stock feel;
   * values < 1 reduce twitch, values > 1 amplify it. Clamped to
   * [MIN_SENSITIVITY, MAX_SENSITIVITY] at load and on every set.
   */
  mouseSensitivity: number;
  /**
   * Multiplier on the touch stick's max angular look-rate. 1.0 = the
   * raw baseline rate; < 1 makes the right stick less twitchy
   * (default on mobile because the stock rate was too fast on small
   * screens), > 1 brings it closer to a flick-aim feel.
   */
  touchSensitivity: number;
}

const DEFAULTS: InputSettings = {
  invertYMouse: false,
  invertYTouch: false,
  mouseSensitivity: 1.0,
  // Intentionally < 1 to address the "mobile aim is too sensitive"
  // user report. See the file-level comment for the rationale + the
  // numbers behind the chosen value.
  touchSensitivity: 0.7,
};

/**
 * Clamp an arbitrary value into the [MIN_SENSITIVITY, MAX_SENSITIVITY]
 * window. Non-finite inputs (NaN / Infinity / strings that came through
 * a stale storage entry) fall back to 1.0 — silently neutral rather than
 * 0 (which would feel broken) or the max (which would feel like a bug).
 */
export function clampSensitivity(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return 1.0;
  if (v < MIN_SENSITIVITY) return MIN_SENSITIVITY;
  if (v > MAX_SENSITIVITY) return MAX_SENSITIVITY;
  return v;
}

/**
 * Effective per-pixel mouse coefficient (BASE × user multiplier). Pure
 * helper so `InputManager.getState` and the unit tests can share a
 * single formula.
 */
export function effectiveMouseSensitivity(s: InputSettings): number {
  return BASE_MOUSE_SENSITIVITY * s.mouseSensitivity;
}

/**
 * Effective max angular velocity (rad/sec) for the touch right stick.
 * `dt` is folded in at the call site rather than here so callers like
 * the mobile aim-assist budget can ask for the rate alone.
 */
export function effectiveTouchLookRate(s: InputSettings): number {
  return BASE_TOUCH_LOOK_RATE * s.touchSensitivity;
}

let cache: InputSettings | null = null;
const listeners = new Set<(s: InputSettings) => void>();

function safeLocalStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

function loadFromStorage(): InputSettings {
  const store = safeLocalStorage();
  if (!store) return { ...DEFAULTS };
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<InputSettings> | null;
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULTS };
    return {
      invertYMouse: !!parsed.invertYMouse,
      invertYTouch: !!parsed.invertYTouch,
      mouseSensitivity: clampSensitivity(
        parsed.mouseSensitivity ?? DEFAULTS.mouseSensitivity,
      ),
      touchSensitivity: clampSensitivity(
        parsed.touchSensitivity ?? DEFAULTS.touchSensitivity,
      ),
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function saveToStorage(s: InputSettings): void {
  const store = safeLocalStorage();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* storage blocked (private mode / quota) — preference is session-only */
  }
}

export function getInputSettings(): InputSettings {
  if (!cache) cache = loadFromStorage();
  return cache;
}

/**
 * Patch one or more settings and persist. Sensitivity fields are clamped
 * here so the caller (sliders, custom UI, dev-tools) cannot stash an
 * out-of-range value. Notifies all listeners with the new full snapshot
 * so the pause menu / input manager / HUD can re-derive state from the
 * latest values.
 */
export function setInputSettings(patch: Partial<InputSettings>): InputSettings {
  const cur = getInputSettings();
  const next: InputSettings = {
    invertYMouse: patch.invertYMouse ?? cur.invertYMouse,
    invertYTouch: patch.invertYTouch ?? cur.invertYTouch,
    mouseSensitivity: patch.mouseSensitivity !== undefined
      ? clampSensitivity(patch.mouseSensitivity)
      : cur.mouseSensitivity,
    touchSensitivity: patch.touchSensitivity !== undefined
      ? clampSensitivity(patch.touchSensitivity)
      : cur.touchSensitivity,
  };
  cache = next;
  saveToStorage(next);
  for (const fn of listeners) {
    try { fn(next); } catch { /* listener crash must not break input */ }
  }
  return next;
}

export function onInputSettingsChange(fn: (s: InputSettings) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Reset the cached snapshot so the next `getInputSettings()` re-reads
 * storage. Exists for tests that need to swap a stubbed localStorage
 * between runs without a full module re-import. Not exported from the
 * public surface elsewhere (production code never wants to forget the
 * live cache).
 */
export function _resetInputSettingsCacheForTests(): void {
  cache = null;
}
